import type { DocumentStore } from '@/features/document/documentStore';
import type { EditorKeyBinding } from '@/features/editor/editorSlots';
import type { Command, CommandContext } from '@/features/types';

import { runCommand, type RunCommandDeps } from './runCommand';

/**
 * THE KEYBOARD, DERIVED FROM THE SAME ARRAY AS THE TOOLBAR — ADR-0014,
 * docs/ARCHITECTURE.md §4.9.
 *
 * This is the DRY requirement of ADR-0014 made mechanical. A `Command` carries its own
 * `shortcut` string in CodeMirror's own syntax; `App.tsx` renders that string onto the button
 * (`shortcutHint`, via `renderShortcut` below) and this file binds the very same string to the
 * very same `run`. There is no second table of shortcuts to fall out of step with the first,
 * and `keymap.test.ts` asserts the remaining failure mode — two commands claiming one key.
 *
 * ═══ WHY THIS FILE RETURNS BINDINGS AND NOT AN `Extension` ═══
 *
 * ARCHITECTURE §4.9 sketches `buildKeymap(…): Extension`. That cannot be written here: gate 2
 * denies `@codemirror/*` outside `src/features/editor/**`, and the composition root is not
 * exempt — it is the layer that imports everything, so it is the layer where an exemption
 * would cost the most. `EditorKeyBinding` in `features/editor/editorSlots.ts` is the
 * structural contract instead; `buildExtensions` wraps the list in `Prec.high(keymap.of(…))`
 * on the other side of the lazy boundary. The difference is one indirection and the property
 * that `npm run build` still splits CodeMirror out of the initial chunk.
 *
 * ═══ A DISABLED COMMAND DOES NOT SWALLOW ITS KEY ═══
 *
 * `run` returns `false` when the command is unavailable or inapplicable, and a CodeMirror
 * binding that returns `false` falls through to the next one. So `Mod-Backspace` on an empty
 * buffer is `deleteGroupBackward` again rather than a key that silently does nothing, and
 * Paste in Firefox — where `isAvailable` is false because the page may never read the
 * clipboard — leaves the editor's own paste path alone. The alternative, swallowing the key
 * because a command exists for it, is how a product teaches users that its shortcuts are
 * unreliable.
 */

/** Every key a command claims: its `shortcut`, plus the equal-rank `shortcutAlias`. */
export function shortcutsOf(command: Command): readonly string[] {
  const keys: string[] = [];
  if (command.shortcut !== undefined) keys.push(command.shortcut);
  if (command.shortcutAlias !== undefined) keys.push(command.shortcutAlias);
  return keys;
}

export interface ShortcutEntry {
  readonly key: string;
  readonly command: Command;
}

/**
 * Flattened `(key, command)` pairs in registry order.
 *
 * Pure and exported so `keymap.test.ts` can interrogate the real registry rather than a
 * reconstruction of it — a duplicate-shortcut test that walks its own copy of the list proves
 * nothing about the application.
 */
export function shortcutEntries(commands: readonly Command[]): readonly ShortcutEntry[] {
  return commands.flatMap((command) => shortcutsOf(command).map((key) => ({ key, command })));
}

/** `key → the commands claiming it`, for every key claimed more than once. Empty is the pass. */
export function duplicateShortcuts(commands: readonly Command[]): ReadonlyMap<string, string[]> {
  const byKey = new Map<string, string[]>();
  for (const { key, command } of shortcutEntries(commands)) {
    const ids = byKey.get(key);
    if (ids === undefined) byKey.set(key, [command.id]);
    else ids.push(command.id);
  }
  return new Map([...byKey].filter(([, ids]) => ids.length > 1));
}

export interface BuildKeymapOptions extends RunCommandDeps {
  /**
   * Read at KEYPRESS time, not at build time. The keymap is installed into a long-lived
   * `EditorView` while `DocumentStore` is owned by `App`; taking the store as a getter is what
   * lets a later Diff View point the same bindings at a second store without rebuilding the
   * editor — and, today, what keeps this list stable across every render.
   */
  readonly getStore: () => DocumentStore;
}

export function buildKeymap(
  commands: readonly Command[],
  ctx: CommandContext,
  options: BuildKeymapOptions,
): readonly EditorKeyBinding[] {
  const { getStore, ...deps } = options;

  return shortcutEntries(commands).map(({ key, command }) => ({
    key,
    preventDefault: true,
    run(): boolean {
      const store = getStore();
      if (command.isAvailable?.(ctx) === false) return false;
      if (command.isEnabled?.(store.getSnapshot()) === false) return false;
      void runCommand(command, ctx, store, deps);
      return true;
    },
  }));
}

// ── firing a shortcut when the editor does NOT have focus ──────────────────────────────────

/**
 * A keystroke, structurally — the four modifier flags and `KeyboardEvent.key`.
 *
 * Structural rather than `KeyboardEvent` for the same reason `EditorKeyBinding` is structural:
 * `keymap.test.ts` runs in the bare-Node `logic` project (gate 3), where there is no DOM and
 * no `KeyboardEvent` to construct. A real event satisfies this shape exactly.
 */
export interface KeyStroke {
  readonly key: string;
  readonly ctrlKey: boolean;
  readonly metaKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
}

/**
 * Does `stroke` press `shortcut`? — the second reader of CodeMirror's key syntax.
 *
 * WHY A SECOND READER EXISTS. Every binding in this product is handed to CodeMirror's keymap,
 * which only sees a keystroke while the EDITOR has focus. Meanwhile every toolbar button
 * prints its own shortcut on its face, and `a11y.actionWithShortcut` reads it out loud. So
 * the moment a user tabbed to a toolbar button — or clicked one, which leaves focus on it —
 * the product was printing a promise it could not keep: ⌘⏎ on the Format button did nothing
 * while the Format button was the focused element.
 *
 * The alternative was to stop printing the shortcuts, which is worse: they are real, they work
 * for the 95 % case, and a product with no discoverable keyboard is a worse product than one
 * whose keyboard needs a second dispatcher. ADR-0024 records the choice.
 *
 * DOUBLE FIRING is the hazard this creates, and it is handled at the call site rather than
 * here: `App.tsx` consults `EditorHandle.hasFocus()` and hands the window listener nothing at
 * all while CodeMirror owns the keystroke. One keymap, two dispatchers, and exactly one of
 * them live at a time.
 *
 * Modifiers not named by the shortcut must NOT be held, so `Mod-Enter` does not fire on
 * `⌘⇧⏎` — which would otherwise make Format and Compact the same key.
 */
export function matchesShortcut(shortcut: string, stroke: KeyStroke, mac: boolean): boolean {
  const parts = shortcut.split('-');
  const base = parts[parts.length - 1];
  if (base === undefined || base.length === 0) return false;

  let mod = false;
  let ctrl = false;
  let shift = false;
  let alt = false;
  let meta = false;

  for (const part of parts.slice(0, -1)) {
    switch (part) {
      case 'Mod':
        mod = true;
        break;
      case 'Ctrl':
        ctrl = true;
        break;
      case 'Shift':
        shift = true;
        break;
      case 'Alt':
        alt = true;
        break;
      case 'Cmd':
      case 'Meta':
        meta = true;
        break;
      default:
        // An unknown modifier word is a typo, and a typo must not match everything.
        return false;
    }
  }

  // `Mod` is ⌘ on Apple platforms and Ctrl everywhere else — CodeMirror's own rule, and the
  // same one `renderShortcut` prints by.
  if (mod) {
    if (mac) meta = true;
    else ctrl = true;
  }

  if (stroke.ctrlKey !== ctrl) return false;
  if (stroke.metaKey !== meta) return false;
  if (stroke.shiftKey !== shift) return false;
  if (stroke.altKey !== alt) return false;

  // `Mod-e` is lower case in CodeMirror's syntax; a browser reports `'e'` unshifted and `'E'`
  // shifted, and the shift flag has already been checked above.
  return base.length === 1
    ? stroke.key.toLowerCase() === base.toLowerCase()
    : stroke.key === base;
}

/**
 * The binding `stroke` fires, or `undefined`.
 *
 * Takes the SAME array `buildKeymap` produced and `EditorPanel` installed, so the window
 * dispatcher cannot know a shortcut the editor does not, or miss one it does.
 */
export function bindingFor(
  bindings: readonly EditorKeyBinding[],
  stroke: KeyStroke,
  mac: boolean,
): EditorKeyBinding | undefined {
  return bindings.find((binding) => matchesShortcut(binding.key, stroke, mac));
}

// ── printing a shortcut for a human ────────────────────────────────────────────────────────

/**
 * `'Mod-Enter'` → `'⌘⏎'` on a Mac, `'Ctrl+Enter'` everywhere else (docs/DESIGN.md §7.4).
 *
 * The platform is decided ONCE, at the composition root, and the rendered string is then
 * carried as data — `ToolbarItem.shortcutHint`, and the `{{shortcut}}` of a toast sentence.
 * Nothing below `src/app/` is allowed to ask which machine it is on: `src/ui/**` is
 * presentation and `src/features/**` is compiled without `navigator` at all (gate 1), which is
 * why `document/commands.ts` currently prints both spellings in its two toast sentences.
 * `PLATFORM_KEYCAPS` below is the substitution that retires those.
 */
const MAC_KEYS: Readonly<Record<string, string>> = {
  Mod: '⌘',
  Cmd: '⌘',
  Meta: '⌘',
  Ctrl: '⌃',
  Shift: '⇧',
  Alt: '⌥',
  Enter: '⏎',
  Backspace: '⌫',
  Delete: '⌦',
  Escape: '⎋',
  ArrowUp: '↑',
  ArrowDown: '↓',
  ArrowLeft: '←',
  ArrowRight: '→',
};

const PC_KEYS: Readonly<Record<string, string>> = {
  Mod: 'Ctrl',
  Cmd: 'Ctrl',
  Meta: 'Win',
};

/**
 * macOS prints modifiers in ONE order, whatever order they were declared in: Control, Option,
 * Shift, Command, then the key — the order Apple's own menus and its Human Interface Guidelines
 * use. `Mod-Shift-Enter` is a CodeMirror binding string and its order is the author's; `⇧⌘⏎` is
 * the platform's spelling of it, and printing `⌘⇧⏎` on a button beside a menu that says `⇧⌘⏎`
 * is a keycap a Mac user has to translate. Windows and Linux DO print in declaration order
 * (`Ctrl+Shift+Enter`), so this table is consulted only on the Mac branch.
 *
 * Anything not a modifier sorts last and keeps its relative order, which is what makes a
 * two-key binding come out as "modifiers, then the key" without this having to know the key.
 */
const MAC_MODIFIER_ORDER: readonly string[] = ['⌃', '⌥', '⇧', '⌘'];

export function renderShortcut(shortcut: string, mac: boolean): string {
  const parts = shortcut.split('-').map((part) => {
    const mapped = mac ? MAC_KEYS[part] : PC_KEYS[part];
    if (mapped !== undefined) return mapped;
    // A bare letter binding (`Mod-e`) is written lower case in CodeMirror's syntax and read
    // upper case by a human. Function keys and words (`F8`, `Enter`) are already right.
    return part.length === 1 ? part.toUpperCase() : part;
  });

  if (!mac) return parts.join('+');

  // `sort` is stable, so the non-modifiers keep the order the binding declared them in.
  const rank = (part: string): number => {
    const index = MAC_MODIFIER_ORDER.indexOf(part);
    return index < 0 ? MAC_MODIFIER_ORDER.length : index;
  };
  // No separator on macOS — `⇧⌘⏎` is how the platform prints it — and `+` elsewhere.
  return parts
    .slice()
    .sort((a, b) => rank(a) - rank(b))
    .join('');
}

/** `true` on macOS and iPadOS. Called once by `App`; everything downstream receives a string. */
export function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  const source = `${navigator.platform ?? ''} ${navigator.userAgent}`;
  return /Mac|iPhone|iPad|iPod/.test(source);
}

/**
 * The two keycaps `features/document/commands.ts` prints inside toast sentences.
 *
 * That file states BOTH spellings (`'Ctrl+Z / ⌘Z'`) because it cannot read `navigator`. Here
 * the platform is known, so the composition root substitutes the single correct one before the
 * sentence is shown — see `resolveKeycaps` in `App.tsx`.
 */
export const PLATFORM_KEYCAPS: Readonly<Record<string, readonly [mac: string, pc: string]>> = {
  'Ctrl+Z / ⌘Z': ['⌘Z', 'Ctrl+Z'],
  'Ctrl+V / ⌘V': ['⌘V', 'Ctrl+V'],
};
